import React, { useState, useEffect, useRef } from 'react';
import './TrustedBySection.css';

// Institution logos from Wikipedia SVGs (direct frontend, no backend dependency)
const FALLBACK_INSTITUTIONS = [
  { name: 'MIT', fullName: 'Massachusetts Institute of Technology', logo: 'https://upload.wikimedia.org/wikipedia/en/4/44/MIT_Seal.svg' },
  { name: 'Stanford', fullName: 'Stanford University', logo: 'https://upload.wikimedia.org/wikipedia/commons/4/4b/Stanford_Cardinal_logo.svg' },
  { name: 'Harvard', fullName: 'Harvard University', logo: 'https://upload.wikimedia.org/wikipedia/commons/7/70/Harvard_University_logo.svg' },
  { name: 'Oxford', fullName: 'University of Oxford', logo: 'https://upload.wikimedia.org/wikipedia/commons/f/ff/Oxford-University-Circlet.svg' },
  { name: 'Cambridge', fullName: 'University of Cambridge', logo: 'https://upload.wikimedia.org/wikipedia/commons/c/c3/Coat_of_Arms_of_the_University_of_Cambridge.svg' },
  { name: 'Berkeley', fullName: 'UC Berkeley', logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a1/Seal_of_University_of_California%2C_Berkeley.svg' },
  { name: 'NASA', fullName: 'NASA', logo: 'https://upload.wikimedia.org/wikipedia/commons/e/e5/NASA_logo.svg' },
  { name: 'ETH Zurich', fullName: 'ETH Zurich', logo: 'https://upload.wikimedia.org/wikipedia/commons/9/99/ETH_Z%C3%BCrich_Logo_black.svg' },
];

/**
 * TrustedBySection - Sticky bottom social proof with scroll reveal
 * Fixed at bottom initially, snaps into natural position when scrolled to
 */
const TrustedBySection = () => {
  const [isFixed, setIsFixed] = useState(true);
  const [institutions, setInstitutions] = useState([]);
  const placeholderRef = useRef(null);
  const sectionRef = useRef(null);

  // Use hardcoded institution logos directly (no backend dependency)
  useEffect(() => {
    setInstitutions(FALLBACK_INSTITUTIONS);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!placeholderRef.current) return;

      const placeholder = placeholderRef.current;
      const rect = placeholder.getBoundingClientRect();
      const sectionHeight = sectionRef.current?.offsetHeight || 80;

      // When the placeholder's top is at or above the bottom of viewport,
      // switch to static position (in normal document flow)
      if (rect.top <= window.innerHeight - sectionHeight) {
        setIsFixed(false);
      } else {
        setIsFixed(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="trusted-by-wrapper" style={{ position: 'relative', zIndex: 50 }}>
      {/* Placeholder - marks the natural position of the section */}
      <div 
        ref={placeholderRef} 
        className="trusted-by-placeholder"
        style={{ height: isFixed ? '80px' : '0' }}
      />

      {/* Actual section - fixed or static based on scroll */}
      <section 
        ref={sectionRef}
        className={`trusted-by-section ${isFixed ? 'trusted-by-fixed' : 'trusted-by-static'}`}
      >
        <div className="trusted-by-container">
          <span className="trusted-by-label">Trusted by top teams</span>

          <div className="trusted-by-logos">
            {institutions.map((institution) => (
              <div
                key={institution._id || institution.name}
                className="trusted-by-logo"
                title={institution.fullName}
              >
                <img
                  src={institution.logo}
                  alt={institution.fullName}
                  loading="lazy"
                />
                <span className="trusted-by-logo-name">{institution.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrustedBySection;
